import React, { useCallback, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { ConnectBalances, ConnectComponentsProvider } from '@stripe/react-connect-js';
import useAccountSession from '../../../connect/use-account-session';
import useConnectInstance from '../../../connect/use-connect-instance';
import useCompanyPayouts from '../../../connect/finance/use-company-payouts';
import PayoutActivityTable from '../../../connect/finance/payout-activity-table';
import FinButton from '../../../connect/finance/fin-button';
import { FinTableSkeleton } from '../../../connect/finance/fin-skeleton';
import COPY from '../../../connect/finance/connect-finance-copy';
import PayoutsFilterBar, { emptyCopyFor, hasClientFilter } from '../../../finance/pages/payouts-filter-bar';

// Spec 2026-10-20 (admin company page: Account Activity shows a Stripe
// company's payouts) §1.2. The restaurant's Payouts page, read-only, inside
// the admin company page's Account Activity tab: Stripe's balances card
// (minted on the `admin` surface, which grants it no feature — no Pay out,
// no schedule editor, no bank change; ConnectAccountSessionPlan on the
// API), then our own list with its filters, export and paging, then the
// way to the ledger. Three things the restaurant page has are not here on
// purpose: rows do not open (D6 — the row target is a restaurant route
// that reads the company from the finance section, a dead end for an
// admin, so `onOpen` is never passed and the table draws no chevron), the
// history link is always drawn (D4 — the ledger's New Entry must stay
// reachable for a Stripe company) and is a button that switches the tab in
// place (D5), and nothing follows a click in Stripe's frame (the card
// cannot pay out, so the list has nothing to catch up with).
export const CARD_UNAVAILABLE =
  "Stripe hasn't finished checking this account; the balance card isn't available yet.";
export const HISTORY_PROMPT = 'Looking for older payouts?';
export const HISTORY_LABEL = 'View payout history';
export const subtitleFor = (name) => `Payouts for ${name || 'this company'}, as the restaurant sees them.`;

// Keys useConnectInstance's effect (never sent to Stripe or to our server —
// see that hook's own comment). `balances` is the one component this tab
// mounts; what the `admin` surface grants it is the server's decision. The
// retry nonce beside it re-runs the build, which calls fetchClientSecret
// again and clears useAccountSession's error on success — the one honest
// re-mint lever, the same one finance-section.js uses.
const BALANCES_COMPONENTS = ['balances'];

const CompanyPayoutsTab = ({ companyId, companyName, status, isDark, onHistory }) => {
  const copy = COPY.restaurant;
  // D3: CONNECT_DIRECT but not READY keeps the list and loses the card. A
  // Stripe component against an account Stripe has not finished checking
  // is the empty-frame case the finance gate avoids, so no session is
  // minted either.
  const ready = status?.connectStatus === 'READY';

  const { fetchSecret, error: sessionError } = useAccountSession(companyId, 'admin');
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const sessionComponents = useMemo(
    () => [...BALANCES_COMPONENTS, `retry-${sessionAttempt}`],
    [sessionAttempt],
  );
  const { instance, error: instanceError } = useConnectInstance(
    ready ? fetchSecret : null,
    sessionComponents,
    isDark,
  );
  // A session that failed wins over an `instance` that merely exists:
  // useConnectInstance builds the instance synchronously, long before
  // Stripe calls fetchClientSecret and discovers the mint was refused.
  const sessionFailure = sessionError || instanceError?.message || '';

  // Stripe reports a load failure inside its own frame through onLoadError
  // (react-connect-js 3.4.4; stripe-notices.js's banner uses the same
  // callback) — the one signal that tells an empty card from one still
  // loading. An older API that grants the admin session no `balances`
  // lands here. The failure belongs to the instance the card was drawn on,
  // not to a boolean: react-connect-js creates the element once, on the
  // instance it first sees, and never re-binds it (useCreateComponent,
  // react-connect.esm.js:90-106), while useConnectInstance swaps in the
  // retry's new instance a render after the nonce moves. So the card stays
  // unmounted until the current instance is not the one that failed, and a
  // Retry from here draws the skeleton — no live Retry, so no second mint —
  // until the new instance exists.
  const [failedInstance, setFailedInstance] = useState(null);
  const [retriedInstance, setRetriedInstance] = useState(null);
  const onLoadError = useCallback(() => setFailedInstance(instance), [instance]);
  const retrySession = useCallback(() => {
    setRetriedInstance(instance);
    setSessionAttempt((n) => n + 1);
  }, [instance]);
  const loadFailed = !!instance && instance === failedInstance;
  const retryPending = !!instance && instance === retriedInstance;

  const {
    filters, changeFilters, upcoming, awaitingUpcoming, tableLoading, list,
    filtered, shown, page, pageCount, previousPage, nextPage, loadList, exportCsv,
  } = useCompanyPayouts(companyId);

  return (
    <section className="space-y-5">
      <div className="rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {/* The tab's own header strip (the ledger's, account-activity-tab.js),
            with its own second line and none of the ledger's controls: no
            BALANCE pill, no Month, no New Entry in this view (spec §1.2). */}
        <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
              <BanknotesIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                Account Activity
              </h2>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {subtitleFor(companyName)}
              </p>
            </div>
          </div>
        </div>

        {/* Inside the card, the restaurant's two Finance sections on the
            Finance palette, in the restaurant's order. Designer brief
            2026-10-20: px-3 below `sm` gives Stripe's frame about 260px at
            360px (px-4 would give 252, against 286 on the restaurant page);
            no background of its own, never bg-fin-canvas — the fin sections
            sit on the card's white (dark: slate-800). */}
        <div className="space-y-5 px-3 py-4 sm:px-6 sm:py-5">
          <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
            {/* The floor is Stripe's loaded height — 20 + 138 + 20, measured on
                fe-test for the restaurant page — so loading does not jump. */}
            <div data-testid="stripe-balances" className="min-h-[178px] p-5">
              {!ready ? (
                <p className="text-[13.5px] text-fin-muted">{CARD_UNAVAILABLE}</p>
              ) : sessionFailure ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-[13.5px] text-fin-red">{copy.sessionFailed}</p>
                  <FinButton onClick={retrySession}>Retry</FinButton>
                </div>
              ) : retryPending ? (
                <FinTableSkeleton rows={4} />
              ) : loadFailed ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-[13.5px] text-fin-muted">{COPY.balance.widgetFailed}</p>
                  <FinButton onClick={retrySession}>Retry</FinButton>
                </div>
              ) : !instance ? (
                <FinTableSkeleton rows={4} />
              ) : (
                // Narrow, around the Stripe mount alone: a throw inside
                // Stripe's component must not blank the company page (the
                // switch's own boundary around this whole view is the second
                // net; connect-support-block.js draws the same line for the
                // same reason).
                <ErrorBoundary
                  fallback={<p className="text-[13.5px] text-fin-muted">{COPY.balance.widgetFailed}</p>}
                >
                  <ConnectComponentsProvider connectInstance={instance}>
                    <ConnectBalances onLoadError={onLoadError} />
                  </ConnectComponentsProvider>
                </ErrorBoundary>
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
            <PayoutsFilterBar filters={filters} onChange={changeFilters} onExport={exportCsv} />
            <PayoutActivityTable
              variant="full"
              rows={awaitingUpcoming ? [] : shown}
              upcoming={upcoming}
              loading={tableLoading}
              error={list.error}
              onRetry={loadList}
              empty={hasClientFilter(filters) ? 'No payouts match these filters.' : emptyCopyFor(filters.date)}
            />
            {filtered.length > 0 ? (
              <div className="flex items-center justify-between gap-3 border-t border-fin-border px-5 py-3">
                <p className="text-[13px] text-fin-muted">{`Showing ${shown.length} of ${filtered.length} payouts`}</p>
                <div className="flex gap-2">
                  <FinButton size="sm" disabled={page <= 1} onClick={previousPage}>
                    Previous
                  </FinButton>
                  <FinButton size="sm" disabled={page >= pageCount} onClick={nextPage}>
                    Next
                  </FinButton>
                </div>
              </div>
            ) : null}
          </section>

          {/* The body's last child, under the list. Preflight resets the
              button, so it reads like the restaurant's link; the focus recipe
              is connect-balance-hero.js's (designer brief 2026-10-20). */}
          <p className="px-1 text-[13px] text-fin-muted">
            {HISTORY_PROMPT}{' '}
            <button
              type="button"
              onClick={onHistory}
              className="-mx-1 rounded px-1 font-semibold text-fin-blue hover:text-fin-blue-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fin-ring"
            >
              {HISTORY_LABEL}
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default CompanyPayoutsTab;
